import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * The One Manager platform's identity (hub contract, docs/04 §3.1 in the hub repo): its name, logo
 * and favicon, set in the hub's admin and read from the hub's public GET /branding. This BMS keeps
 * its own look otherwise; the business's own branding is BrandingContext.
 *
 * VITE_HUB_CONTENT_URL is the hub content service's URL. Without it, or until the hub has answered
 * once, the platform has no name (messages say "the platform"): nothing here is hard-coded.
 * Its contact details are what a business shows until it sets its own (BrandingContext).
 *
 * Its colours are this BMS's default colours: the ones the admin hub set for this BMS
 * (appColors, by this BMS's registry id VITE_HUB_BMS_ID), or else the platform's. A business can
 * choose its own instead in its branding settings (BrandingContext).
 */
const HUB_CONTENT_URL = (import.meta.env.VITE_HUB_CONTENT_URL || '').replace(/\/$/, '');
const HUB_BMS_ID = import.meta.env.VITE_HUB_BMS_ID || 'eldaved-tile';
const CACHE_KEY = 'hub-platform-branding';
const CACHE_MS = 5 * 60 * 1000;

export const DEFAULT_PLATFORM = {
  name: '',
  logo: { mode: 'default', imageUrl: null, imageDarkUrl: null, text: '', alt: '' },
  contact: { email: '', phone: '', address: '' },
  faviconUrl: null,
  // Known once the hub has answered: no colour is built in (utils/brandColors.js NEUTRAL until then)
  primaryColor: null,
  accentColor: null,
  appColors: {}
};

const PlatformBrandingContext = createContext(DEFAULT_PLATFORM);

export const usePlatformBranding = () => useContext(PlatformBrandingContext);

/** The platform's name for sentences: "the platform" until the hub has answered. */
export const platformNameOf = (platform) => platform?.name || 'the platform';
export const usePlatformName = () => platformNameOf(useContext(PlatformBrandingContext));

/** This BMS's default colours from One Manager: its own if the admin hub set them, else the platform's. */
export const platformColoursFor = (platform) =>
  platform?.appColors?.[HUB_BMS_ID] || {
    primaryColor: platform?.primaryColor || null,
    accentColor: platform?.accentColor || null
  };

function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return cached?.value ? cached : null;
  } catch {
    return null;
  }
}

/** The browser tab follows the platform: its name as the title, its favicon as the icon. */
function applyToTab(platform) {
  if (platform.name) document.title = platform.name;
  if (platform.faviconUrl) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.removeAttribute('type');
    link.href = platform.faviconUrl;
  }
}

export function PlatformBrandingProvider({ children }) {
  const [platform, setPlatform] = useState(() => readCache()?.value ?? DEFAULT_PLATFORM);

  useEffect(() => {
    applyToTab(platform);
  }, [platform]);

  useEffect(() => {
    const cached = readCache();
    if (!HUB_CONTENT_URL || (cached && Date.now() - cached.at < CACHE_MS)) return undefined;
    const controller = new AbortController();
    fetch(`${HUB_CONTENT_URL}/branding`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || typeof data.name !== 'string') return;
        const value = {
          ...DEFAULT_PLATFORM,
          ...data,
          logo: { ...DEFAULT_PLATFORM.logo, ...(data.logo || {}) },
          contact: { ...DEFAULT_PLATFORM.contact, ...(data.contact || {}) }
        };
        setPlatform(value);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ value, at: Date.now() }));
        } catch {
          // fine: shown for this visit
        }
      })
      .catch(() => undefined); // the hub is asleep or unreachable: keep what we have
    return () => controller.abort();
  }, []);

  return <PlatformBrandingContext.Provider value={platform}>{children}</PlatformBrandingContext.Provider>;
}
